import { Subtenant } from '@bioliberty/lifemind';
import { useForm } from '@tanstack/react-form';
import { CircleHelp } from 'lucide-react';
import { ChangeEvent, PropsWithChildren, useState } from 'react';
import z from 'zod';
import { useUpdateSubtenantMutation } from '../../api';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { NumberInput } from '../../components/Input/NumberInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ErrorMessage, InputLabel, TextInput } from '../../components/Input/TextInput';
import { Switch } from '../../components/Switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { useToaster } from '../../hooks/useToaster';
import { timezones } from '../../util/timezones';

interface EditClinicDialogProps extends PropsWithChildren {
  clinic: Subtenant;
}

const formValuesSchema = z.object({
  label: z.string().min(1, 'A label is required'),
  lifeglovLinerStockLowThreshold: z.number().int().nonnegative().nullable(),
  monthlyCapacity: z.number().int().positive().nullable(),
  timezone: z.string().min(1, 'A timezone is required'),
  inactive: z.boolean()
});

export function EditClinicDialog({ clinic, children }: EditClinicDialogProps) {
  const [open, setOpen] = useState(false);

  const [updateSubtenant] = useUpdateSubtenantMutation();

  const toast = useToaster();

  const form = useForm({
    defaultValues: {
      label: clinic.label,
      lifeglovLinerStockLowThreshold: clinic.lifeglovLinerStockLowThreshold,
      monthlyCapacity: clinic.monthlyCapacity,
      timezone: clinic.timezone,
      inactive: clinic.inactive
    },
    validators: {
      onChange: formValuesSchema
    },
    onSubmit: async ({ value }) => {
      try {
        await updateSubtenant({ name: clinic.name, ...value }).unwrap();
        toast.success('Clinic updated successfully.');
        setOpen(false);
      } catch {
        toast.error('Failed to update clinic. Please try again.');
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        form.reset();
        setOpen(open);
      }}
    >
      {children}

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {clinic.name}</DialogTitle>
        </DialogHeader>

        <form.Field name="label">
          {(field) => (
            <TextInput
              initialValue={field.state.value}
              label="Name*"
              name={field.name}
              onChange={field.handleChange}
              errorMessage={field.state.meta.errors[0]?.message}
              autoFocus
            />
          )}
        </form.Field>

        <form.Field name="timezone">
          {(field) => (
            <div className="flex flex-col">
              <InputLabel htmlFor={field.name}>Time Zone*</InputLabel>
              <Select value={field.state.value} onValueChange={field.handleChange}>
                <SelectTrigger id={field.name} className="w-full">
                  <SelectValue placeholder="Select a time zone" />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors[0]?.message && (
                <ErrorMessage>{field.state.meta.errors[0]?.message}</ErrorMessage>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="lifeglovLinerStockLowThreshold">
          {(field) => (
            <NumberInput
              label={
                <span className="flex items-center gap-1">
                  <span>Low Stock Threshold</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleHelp className="w-4" />
                    </TooltipTrigger>
                    <TooltipContent className="flex items-center gap-2">
                      <span>
                        An email notification will be sent to admins when the Lifeglov liner stock reaches this
                        threshold.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </span>
              }
              name={field.name}
              value={field.state.value ?? undefined}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                field.handleChange(value);
              }}
              errorMessage={field.state.meta.errors[0]?.message}
              step={1}
              min={0}
            />
          )}
        </form.Field>

        <form.Field name="monthlyCapacity">
          {(field) => (
            <NumberInput
              label={
                <span className="flex items-center gap-1">
                  <span>Monthly Census</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleHelp className="w-4" />
                    </TooltipTrigger>
                    <TooltipContent className="flex items-center gap-2">
                      <span>The number of patients this clinic is treating in a month.</span>
                    </TooltipContent>
                  </Tooltip>
                </span>
              }
              name={field.name}
              value={field.state.value ?? undefined}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                field.handleChange(value);
              }}
              errorMessage={field.state.meta.errors[0]?.message}
              step={1}
              min={1}
            />
          )}
        </form.Field>

        <form.Field name="inactive">
          {(field) => (
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-1">
                <span className="text-sm">Status</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="w-4" />
                  </TooltipTrigger>
                  <TooltipContent className="flex items-center gap-2">
                    <span>Inactive clinics will not be included in Lifemind metrics and dashboards.</span>
                  </TooltipContent>
                </Tooltip>
              </span>
              <Switch
                checked={!field.state.value}
                onChange={(value) => field.handleChange(!value)}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </div>
          )}
        </form.Field>

        <DialogFooter>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <Button
                variant="accent"
                onClick={form.handleSubmit}
                disabled={isSubmitting || !canSubmit}
                isLoading={isSubmitting}
              >
                Submit
              </Button>
            )}
          </form.Subscribe>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
